import {
  type OrderFiscalizationReadinessFieldsFragment,
  OrderFiscalKktStatus,
  OrderFiscalTerminalStatus,
} from '@shared/api/graphql/gql/graphql';

export const TERMINAL_RECONNECT_INSTRUCTION =
  'Терминал не отвечает. Если результат предыдущей оплаты неизвестен, ничего не перезапускайте — свяжитесь с управляющим. Выньте USB-кабель терминала из компьютера, подождите 10 секунд и вставьте обратно в тот же разъём. Не помогло — перезагрузите компьютер и снова откройте Mors. Связи всё ещё нет — свяжитесь с управляющим.';

export type FiscalizationDisplayStatus =
  'cash_only' | 'degraded' | 'disconnected' | 'not_configured' | 'offline' | 'paused' | 'ready';
export type FiscalizationStatusAudience = 'admin' | 'manager';

export interface FiscalizationStatusPresentation {
  description: string;
  label: string;
  status: FiscalizationDisplayStatus;
  tone: 'danger' | 'neutral' | 'success' | 'warning';
}

/**
 * Флаги и статусы берутся из codegen, чтобы правка схемы ломала типы здесь.
 * `setting`/`last_workstation` остаются структурными: разные запросы выбирают
 * разный набор их полей, а функции нужно лишь наличие связи.
 */
type FiscalizationReadinessLike = Pick<
  OrderFiscalizationReadinessFieldsFragment,
  'effective_enabled' | 'kkt_status' | 'setting_enabled' | 'terminal_status'
> & {
  last_workstation?: { active: boolean } | null;
  paused?: boolean;
  setting?: object | null;
};

export function fiscalizationStatusPresentation(
  readiness: FiscalizationReadinessLike | null | undefined,
  audience: FiscalizationStatusAudience,
): FiscalizationStatusPresentation {
  if (!readiness || !readiness.setting || !readiness.last_workstation) {
    return {
      description:
        audience === 'admin'
          ? 'Принимайте оплату как обычно — чек Mors не напечатает.'
          : 'Подключите рабочее место и выполните тестовую печать.',
      label: audience === 'admin' ? 'Касса не подключена' : 'Касса не настроена',
      status: 'not_configured',
      tone: 'neutral',
    };
  }

  if (readiness.paused === true) {
    return {
      description:
        audience === 'admin'
          ? 'Принимайте оплату как обычно, чек пробейте на самой кассе.'
          : 'Оплата принимается, чеки в Mors не пробиваются.',
      label: audience === 'admin' ? 'Чеки временно не печатаются' : 'Фискализация на паузе',
      status: 'paused',
      tone: 'warning',
    };
  }

  if (!readiness.last_workstation.active) {
    return {
      description:
        audience === 'admin'
          ? 'Принимайте оплату как обычно — чек Mors не напечатает.'
          : 'Оплата принимается без чеков в Mors. Включить кассу можно на кассовом компьютере.',
      label: 'Касса выключена',
      status: 'disconnected',
      tone: 'neutral',
    };
  }

  if (readiness.kkt_status !== OrderFiscalKktStatus.Ready) {
    return {
      description:
        audience === 'admin'
          ? 'Пока касса не отвечает, принять оплату не получится.'
          : 'ККТ не подтвердила готовность. Приём оплаты временно заблокирован.',
      label: audience === 'admin' ? 'Касса не отвечает' : 'Касса недоступна',
      status: 'offline',
      tone: 'danger',
    };
  }

  if (!readiness.setting_enabled || !readiness.effective_enabled) {
    return {
      description:
        audience === 'admin'
          ? 'Чеки пока не печатаются, принимайте оплату как обычно.'
          : 'Привязка сохранена, но обязательная фискализация ещё не включена.',
      label: audience === 'admin' ? 'Касса ещё не включена' : 'Ожидает включения',
      status: 'degraded',
      tone: 'warning',
    };
  }

  if (readiness.terminal_status !== OrderFiscalTerminalStatus.Ready) {
    return {
      description: TERMINAL_RECONNECT_INSTRUCTION,
      label: audience === 'admin' ? 'Сейчас только наличные' : 'Только наличные',
      status: 'cash_only',
      tone: 'warning',
    };
  }

  return {
    description:
      audience === 'admin'
        ? 'Можно принимать оплату наличными и картой.'
        : 'ККТ и банковский терминал готовы к работе.',
    label: 'Касса готова',
    status: 'ready',
    tone: 'success',
  };
}
